import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { packPlaybackNodeLanes, type ProjectNode } from '../lib/project'
import type { PlayerStatus } from '../player/engine'

const formatTime = (seconds: number) => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  const remainder = (Math.max(0, seconds) % 60).toFixed(1).padStart(4, '0')
  return `${String(minutes).padStart(2, '0')}:${remainder}`
}

type Props = {
  nodes: Array<ProjectNode & { end: number }>
  duration: number
  actualTime: number
  currentNodeId: string | null
  queuedNodeId: string | null
  status: PlayerStatus
  playbackIntent: boolean
  visitedNodeIds: ReadonlySet<string>
  countdown: number | null
  disabled: boolean
  error: string
  onSelect: (nodeId: string) => void
  onRetryPlayer: () => void
  youtubeVideoId: string
  immersiveEnabled: boolean
  immersiveActive: boolean
  onImmersiveOpen?: () => void
}

type TextRange = {
  start: number
  end: number
  collapsed: boolean
}

/**
 * Bands retain their authored intervals. Text gets a separate, deterministic
 * interval so every overlapping pair meets at its overlap midpoint.
 */
function calculateTextRanges(nodes: Array<ProjectNode & { end: number }>) {
  const ranges = new Map<string, TextRange>()
  const lowerBounds = nodes.map((node) => node.start)
  const upperBounds = nodes.map((node) => node.end)

  for (let earlierIndex = 0; earlierIndex < nodes.length; earlierIndex += 1) {
    const earlier = nodes[earlierIndex]!
    for (let laterIndex = earlierIndex + 1; laterIndex < nodes.length; laterIndex += 1) {
      const later = nodes[laterIndex]!
      const overlapStart = Math.max(earlier.start, later.start)
      const overlapEnd = Math.min(earlier.end, later.end)
      if (overlapStart >= overlapEnd) continue

      const midpoint = (overlapStart + overlapEnd) / 2
      upperBounds[earlierIndex] = Math.min(upperBounds[earlierIndex]!, midpoint)
      lowerBounds[laterIndex] = Math.max(lowerBounds[laterIndex]!, midpoint)
    }
  }

  nodes.forEach((node, index) => {
    const start = lowerBounds[index]!
    const end = upperBounds[index]!
    ranges.set(node.id, { start, end, collapsed: start >= end })
  })
  return ranges
}

export function WholeSongSegmentNavigator({
  nodes,
  duration,
  actualTime,
  currentNodeId,
  queuedNodeId,
  status,
  playbackIntent,
  visitedNodeIds,
  countdown,
  disabled,
  error,
  onSelect,
  onRetryPlayer,
  youtubeVideoId,
  immersiveEnabled,
  immersiveActive,
  onImmersiveOpen,
}: Props) {
  const packed = useMemo(() => packPlaybackNodeLanes(nodes, duration), [duration, nodes])
  const naturalNodes = packed.nodes
  const textRanges = useMemo(() => calculateTextRanges(naturalNodes), [naturalNodes])
  const listRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef(new Map<string, HTMLButtonElement>())
  const [rovingNodeId, setRovingNodeId] = useState(() => currentNodeId ?? naturalNodes[0]?.id ?? '')
  const clampedTime = Math.max(0, Math.min(duration, actualTime))
  const currentNode = currentNodeId ? naturalNodes.find((node) => node.id === currentNodeId) ?? null : null

  useEffect(() => {
    if (rovingNodeId && naturalNodes.some((node) => node.id === rovingNodeId)) return
    setRovingNodeId(currentNodeId ?? naturalNodes[0]?.id ?? '')
  }, [currentNodeId, naturalNodes, rovingNodeId])

  useEffect(() => {
    if (listRef.current?.contains(document.activeElement)) return
    setRovingNodeId(currentNodeId ?? naturalNodes[0]?.id ?? '')
  }, [currentNodeId, naturalNodes])

  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, nodeId: string) => {
    const index = naturalNodes.findIndex((node) => node.id === nodeId)
    let nextIndex = index
    if (event.key === 'ArrowRight') nextIndex = Math.min(naturalNodes.length - 1, index + 1)
    else if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1)
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = naturalNodes.length - 1
    else return
    event.preventDefault()
    const next = naturalNodes[nextIndex]
    if (!next) return
    setRovingNodeId(next.id)
    buttonRefs.current.get(next.id)?.focus()
  }

  const statusText = status === 'unloaded'
    ? '播放器準備中'
    : status === 'buffering'
      ? '正在緩衝…'
      : status === 'switching' && currentNode
        ? `切換至「${currentNode.label}」…`
        : status === 'ended'
          ? '已到最後一段，播放已停止。'
          : !currentNode
            ? '目前在未分段位置'
            : ''
  const valueText = `${formatTime(clampedTime)} / ${formatTime(duration)}，${currentNode ? `目前段落：${currentNode.label}` : '目前在未分段位置'}`

  return (
    <section className={`whole-song-navigator whole-song-navigator--lanes-${packed.laneCount}`} aria-label="整首段落">
      <div className="whole-song-navigator__summary">
          <strong>整首段落 · {naturalNodes.length} 段</strong>
          <span>{formatTime(clampedTime)} / {formatTime(duration)}</span>
          {statusText && <span className="whole-song-navigator__state" role="status">{statusText}</span>}
      </div>
      {immersiveEnabled && !immersiveActive && (
        <button className="immersive-entry" type="button" onClick={onImmersiveOpen} disabled={!onImmersiveOpen || disabled} aria-label={disabled ? '播放器暫時無法進入沉浸播放。' : '進入沉浸播放'}>
          <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          <span>進入沉浸播放</span>
        </button>
      )}
      <div
        className="whole-song-timeline"
        ref={listRef}
        role="progressbar"
        aria-label="整首播放位置"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={clampedTime}
        aria-valuetext={valueText}
      >
        {Array.from({ length: packed.laneCount }, (_, lane) => <span className="whole-song-timeline__lane" aria-hidden="true" key={lane} style={{ '--segment-lane': lane } as CSSProperties} />)}
        {packed.nodes.map((node) => <span className={`whole-song-rail__band${node.aggregated ? ' whole-song-rail__band--aggregate' : ''}`} aria-hidden="true" data-node-id={node.id} data-segment-lane={node.visualLane} key={`band:${node.id}`} style={{ '--segment-left': `${node.start / duration * 100}%`, '--segment-width': `${(node.end - node.start) / duration * 100}%`, '--segment-lane': node.visualLane } as CSSProperties} />)}
        {packed.overlapGroups.map((group) => <span aria-hidden="true" className="whole-song-rail__overflow-count" key={`${group.start}:${group.end}`} style={{ '--segment-left': `${group.start / duration * 100}%`, '--segment-lane': 1 } as CSSProperties}>+{group.overflowCount}</span>)}
        {naturalNodes.map((node) => {
          const packedNode = packed.nodes.find((candidate) => candidate.id === node.id)!
          const textRange = textRanges.get(node.id)!
          const lyric = node.openingLyric?.trim() || '未設定歌詞'
          const switching = status === 'switching' && node.id === currentNodeId
          const current = node.id === currentNodeId
          const playing = current && (status === 'playing' || status === 'buffering' && playbackIntent)
          const ended = current && status === 'ended'
          const queued = !current && node.id === queuedNodeId
          const visited = !current && !queued && visitedNodeIds.has(node.id)
          const visibleState = switching
            ? '切換中'
            : playing
              ? countdown ?? '播放中'
              : ended
                ? '已結束'
                : current
                  ? '已定位'
                  : queued
                    ? '已預選'
                    : visited
                      ? '已播'
                      : ''
          const stateClass = switching ? 'switching' : playing ? 'playing' : ended ? 'ended' : current ? 'current' : queued ? 'queued' : visited ? 'visited' : 'selectable'
          const stateDescription = typeof visibleState === 'number' ? `剩餘 ${visibleState} 秒` : visibleState || '跳至段落'
          const fullLabel = `${node.label}，${lyric}，${formatTime(node.start)} - ${formatTime(node.end)}，${stateDescription}${textRange.collapsed ? '，文字空間不足，仍可跳至段落' : ''}`
          return (
            <button
              type="button"
              className={`whole-song-card whole-song-card--${stateClass}`}
              key={node.id}
              ref={(element) => { if (element) buttonRefs.current.set(node.id, element); else buttonRefs.current.delete(node.id) }}
              tabIndex={node.id === rovingNodeId ? 0 : -1}
              title={fullLabel}
              aria-label={fullLabel}
              aria-current={current ? 'true' : undefined}
              aria-busy={switching || undefined}
              data-node-id={node.id}
              data-segment-lane={packedNode.visualLane}
              data-text-start={textRange.start}
              data-text-end={textRange.end}
              disabled={disabled}
              style={{
                '--segment-left': `${(textRange.collapsed ? Math.min(node.end, Math.max(node.start, textRange.start)) : textRange.start) / duration * 100}%`,
                '--segment-width': textRange.collapsed ? '1px' : `${(textRange.end - textRange.start) / duration * 100}%`,
                '--segment-lane': packedNode.visualLane,
              } as CSSProperties}
              onFocus={() => setRovingNodeId(node.id)}
              onKeyDown={(event) => moveFocus(event, node.id)}
              onClick={() => {
                setRovingNodeId(node.id)
                onSelect(node.id)
              }}
            >
              {!textRange.collapsed && <>
                <span className="whole-song-card__top"><strong>{node.label}</strong>{visibleState !== '' && <small aria-hidden={typeof visibleState === 'number' ? undefined : 'true'} aria-label={typeof visibleState === 'number' ? `剩餘 ${visibleState} 秒` : undefined}>{visibleState}</small>}</span>
                <span className={`whole-song-card__lyric${node.openingLyric?.trim() ? '' : ' whole-song-card__lyric--empty'}`}>{lyric}</span>
              </>}
            </button>
          )
        })}
        <span aria-hidden="true" className="whole-song-rail__playhead" style={{ '--segment-left': `${clampedTime / duration * 100}%` } as CSSProperties} />
      </div>
      {error && (
        <p className="whole-song-navigator__error" role="alert">
          <span>{error}</span>
          {error === 'YouTube Player API 未載入' && <button className="text-button" type="button" onClick={onRetryPlayer}>重新載入播放器</button>}
          {disabled && <a href={`https://www.youtube.com/watch?v=${youtubeVideoId}`} target="_blank" rel="noreferrer">在 YouTube 開啟</a>}
        </p>
      )}
    </section>
  )
}
